import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query';
import type { AppApiError } from '@core/errors/api-errors.ts';

function extractMessage(error: unknown): string {
  const err = error as AppApiError | undefined;
  if (!err) return 'Error desconocido';

  // ASP.NET Core validation errors: { errors: { Field: ['msg'] } }
  const validation = err.details?.errors;
  if (validation && typeof validation === 'object') {
    const messages = Object.values(validation as Record<string, string[]>)
      .flat()
      .join(' · ');
    if (messages) return messages;
  }

  return err.message || 'Error al procesar la solicitud';
}

function emitErrorToast(error: unknown) {
  const message = extractMessage(error);
  window.dispatchEvent(
    new CustomEvent('cp:toast', { detail: { kind: 'error', title: 'Error', message } }),
  );
}

export const queryClient = new QueryClient({
  // Global handler for ALL mutations — covers creates, updates, deletes
  mutationCache: new MutationCache({
    onError: (error) => emitErrorToast(error),
  }),

  // Global handler for background query failures
  queryCache: new QueryCache({
    onError: (error) => {
      const apiError = error as AppApiError;
      // 401 is handled by the auth redirect; 404 by empty states — skip those
      if (apiError?.status === 401 || apiError?.status === 404) return;
      emitErrorToast(error);
    },
  }),

  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount, error) => {
        const apiError = error as AppApiError;
        if (apiError?.code === 'UNAUTHORIZED' || apiError?.code === 'FORBIDDEN') {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});
